import {
  MESSAGE_SENDER,
  formatMessageDateTime,
  isMessageRead,
  messageBodyPlain,
  messageStatusLabel,
  type PlayerMessage,
} from '@/domain/messages';
import { cn } from '@/lib/cn';

export interface MessageDetailsPanelProps {
  details: PlayerMessage;
  fromLabel?: string;
  className?: string;
}

/**
 * Account message detail panel (Figma MessageDetailsPanel). Back navigation
 * lives on the screen, not in this panel.
 */
export function MessageDetailsPanel({
  details,
  fromLabel = `From: ${MESSAGE_SENDER}`,
  className,
}: MessageDetailsPanelProps) {
  const read = isMessageRead(details);
  const status = messageStatusLabel(details);
  const date = formatMessageDateTime(details.Timestamp);
  const body = messageBodyPlain(details);

  return (
    <div
      className={cn(
        'flex w-full max-w-[560px] flex-col gap-5 rounded-md bg-bg-surface p-6',
        className,
      )}
    >
      <header className="flex w-full flex-col gap-2 overflow-hidden">
        <div className="flex w-full items-center gap-3">
          <p className="shrink-0 text-[12px] text-text-secondary whitespace-nowrap">
            {fromLabel}
          </p>
          <div className="min-w-px flex-1" />
          <span
            className={cn(
              'rounded-full bg-bg-elevated px-2 py-0.5 text-[11px] font-bold whitespace-nowrap',
              read ? 'text-text-secondary' : 'text-action-primary',
            )}
          >
            {status}
          </span>
        </div>
        <h2 className="text-[20px] leading-[26px] font-bold text-text-primary">
          {details.Title || 'Message'}
        </h2>
        {date ? <p className="text-[12px] text-text-secondary">{date}</p> : null}
      </header>

      <div className="h-px w-full bg-border-subtle" />

      {body ? (
        <div className="w-full text-[14px] leading-[22px] whitespace-pre-wrap text-text-primary">
          {body}
        </div>
      ) : null}
    </div>
  );
}
